import { useState } from "react"

import "./index.scss"
import { ConfigContainer } from "components"
import { Container, Title, Text, Select, Number } from "components/Form"
import useT from "utils/hooks/useT"

// Usability configuration component that allows editing of a maintenance record
export default function UsabilityConfig({ maintenanceItem, assetName, onCancel, onSave, onDelete, isCreating = false, readOnly = false }) {
  const { t } = useT("common")
  // Initialize the record we're editing with either the provided item or default values
  const [maintenanceRecord, setMaintenanceRecord] = useState(
    maintenanceItem || {
      name: "",
      endDate: new Date().toISOString().split('T')[0], // Default to today
      recurrenceValue: 1,
      recurrenceUnit: "week",
      reminder: 0
    }
  );

  const originalName = maintenanceItem ? maintenanceItem.name : "";

  // Create a collection for the unit options that is compatible with the Select component
  const unitCollection = [
    { id: "day", name: t("days") },
    { id: "week", name: t("weeks") },
    { id: "month", name: t("months") },
    { id: "year", name: t("years") }
  ];

  // Handle changes to the maintenance record fields
  const handleRecordChange = (field, value) => {
    // If the field is recurrenceUnit and value is an array, extract the first element
    if (field === 'recurrenceUnit' && Array.isArray(value) && value.length > 0) {
      value = value[0];
    }

    setMaintenanceRecord(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form submission
  const handleSubmit = () => {
    if (onSave) {
      onSave(maintenanceRecord);
    }
    return true; // Indicate successful submission to the ConfigContainer
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(maintenanceItem);
    }
  };

  const title = isCreating ?
    t("addNewMaintenanceFor", { assetName: assetName || t("instrument") }) :
    t("editMaintenanceFor", { name: originalName, assetName: assetName || t("instrument") });

  return (
    <ConfigContainer
      title={title}
      cancelHandler={onCancel}
      submitHandler={handleSubmit}
      deleteHandler={isCreating ? null : handleDelete}
      deleteTitle={t("delete")}
      confirmDeletionText={t("confirmDeleteMaintenance")}
      disabled={readOnly}
      canCancelWhenDisabled
    >
      {/* Name field in its own container */}
      <Container className="maintenance-field-container">
        <Title>{t("name")} <div className="red-text">*</div></Title>
        <Text
          value={maintenanceRecord.name}
          required
          disabled={readOnly}
          onChange={(value) => handleRecordChange('name', value)}
          placeholder={t("maintenanceName")}
        />
      </Container>

      {/* End date field in its own container */}
      <Container className="maintenance-field-container">
        <Title>{t("endDate")} <div className="red-text">*</div></Title>
        <Text
          value={maintenanceRecord.endDate}
          disabled={readOnly}
          onChange={(value) => handleRecordChange('endDate', value)}
          placeholder={t("dateFormatYyyyMmDd")}
          type="date"
        />
      </Container>

      {/* Custom layout for recurrence field to ensure inline display */}
      <div className="custom-recurrence-container">
        <div className="recurrence-label">
          <Title>{t("recurrence")} <div className="red-text">*</div></Title>
        </div>
        <div className="recurrence-inputs-wrapper">
          <div className="recurrence-number">
            <Number
              value={maintenanceRecord.recurrenceValue}
              disabled={readOnly}
              onChange={(value) => handleRecordChange('recurrenceValue', value)}
              min={1}
            />
          </div>
          <div className="recurrence-select">
            <Select
              value={maintenanceRecord.recurrenceUnit}
              disabled={readOnly}
              onChange={(value) => handleRecordChange('recurrenceUnit', value)}
              collection={unitCollection}
              isMulti={false}
            />
          </div>
        </div>
      </div>

      <Container className="maintenance-field-container">
        <Title>{t("reminderDaysBeforeEndDate")}</Title>
        <Number
          value={maintenanceRecord.reminder}
          disabled={readOnly}
          onChange={(value) => handleRecordChange('reminder', value)}
          min={0}
          placeholder={t("daysBeforeExpirationWarning")}
        />
      </Container>
    </ConfigContainer>
  );
}
